"use client";

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader } from "lucide-react";
export default function MainHero() {
  const [isOpen, setIsOpen] = useState(false);
  const [heroBgUrl, setHeroBgUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  useEffect(() => {
    const fetchHeroBackground = async () => {
      try {
        const response = await fetch("/wp-json/wp/v2/settings");
        if (response.ok) {
          const settings = await response.json();
          if (settings.hero_background_url) {
            setHeroBgUrl(settings.hero_background_url);
          } else {
            setHeroBgUrl("https://wpvc-images.s3.us-east-1.amazonaws.com/images/1504672/img/hero_restaurant_background.png");
          }
        } else {
          setHeroBgUrl("https://wpvc-images.s3.us-east-1.amazonaws.com/images/1504672/img/hero_restaurant_background.png");
        }
      } catch (error) {
        console.error("Error fetching hero background:", error);
        setHeroBgUrl("https://wpvc-images.s3.us-east-1.amazonaws.com/images/1504672/img/hero_restaurant_background.png");
      } finally {
        setIsLoading(false);
      }
    };
    fetchHeroBackground();
  }, []);
  return <section id="главен-герой" ref={ref} className="dark relative min-h-[80vh] md:min-h-screen flex items-center justify-center overflow-hidden" style={{
    backgroundColor: !heroBgUrl && !isLoading ? "#e8d5c4" : "hsl(var(--background))"
  }}>
      {heroBgUrl && <img src="https://mishmashinterpred.com/wp-content/uploads/2026/02/IMG-6ce3c7fc10e2caa4da36f325072c2724-V.webp" data-wvc-srcset={`${heroBgUrl}-wvc-srcset`} data-wvc-sizes={`${heroBgUrl}-sizes`} alt="Main Hero Background" fetchpriority="high" loading="eager" decoding="async" className="absolute inset-0 w-full h-full object-cover -z-10" />}
      
      <div className="absolute inset-0 bg-dark-background/70" aria-hidden="true" />

      <div className={`container relative z-10 text-center transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
        <h1 className="text-[48px] font-display font-bold leading-[1.1] tracking-tight mb-6 !text-[hsl(0,0%,100%)] md:text-[64px] lg:text-[72px]">Бързо. Домашно. Невероятно вкусно.</h1>

        <p className="text-[18px] leading-[1.6] max-w-2xl mx-auto mb-10 !text-[hsl(0,0%,100%)] md:text-[20px] lg:text-[22px]">Модерно заведение за бързо хранене в София "Миш-Маш /интерпред/" – вкусна домашна храна от висококачествени подбрани продукти</p>

        <Button onClick={() => {
        const formElement = document.getElementById("full_order_form_home");
        if (formElement) {
          formElement.dispatchEvent(new Event("submit", {
            cancelable: true,
            bubbles: true
          }));
        } else {
          setIsOpen(true);
        }
      }} size="lg" className="btn-primary text-[17px] px-10 py-4 h-auto shadow-medium hover:shadow-clay-hover cursor-pointer">
          Поръчай сега
        </Button>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Направете поръчка</DialogTitle>
            <DialogDescription>
              Попълнете формата по-долу и ние ще се свържем с вас за потвърждение.
            </DialogDescription>
          </DialogHeader>
          <FormProvider formId="full_order_form_home" sectionName="главен-герой" defaultValues={{
          customer_name: "",
          phone_number: "",
          email_address: "",
          order_details: "",
          order_type: "",
          address: ""
        }} validationRules={{
          customer_name: {
            required: true,
            minLength: 2,
            maxLength: 100
          },
          phone_number: {
            required: true,
            pattern: "phone"
          },
          email_address: {
            pattern: "email"
          },
          order_details: {
            required: true,
            minLength: 5
          },
          order_type: {
            required: true
          },
          address: {
            required: true
          }
        }} submitText="Изпрати поръчка" successMessage="Благодарим ви! Ще се свържем с вас скоро за потвърждение." errorMessage="Неуспешно изпращане. Моля, опитайте отново.">
            <OrderFormContent onSuccess={() => setIsOpen(false)} />
          </FormProvider>
        </DialogContent>
      </Dialog>
    </section>;
}
function OrderFormContent({
  onSuccess
}: {
  onSuccess: () => void;
}) {
  const navigate = useNavigate();
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    control
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      onSuccess();
      window.location.href = 'https://mishmashinterpred.com/blagodarq-za-porachkata';
    }
  }, [isSubmitted, submitError, onSuccess]);
  if (isSubmitted && !submitError) {
    return null;
  }
  return <Form onSubmit={handleSubmit}>
      <FormField control={control} name="customer_name" rules={{
      required: "Името е задължително",
      minLength: {
        value: 2,
        message: "Минимум 2 символа"
      },
      maxLength: {
        value: 100,
        message: "Максимум 100 символа"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Име и фамилия</FormLabel>
            <FormControl>
              <Input placeholder="Иван Петров" {...field} />
            </FormControl>
            <FormDescription>
              Въведете пълното си име
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone_number" rules={{
      required: "Телефонният номер е задължителен",
      pattern: {
        value: /^[+]?[\d\s()-]+$/,
        message: "Невалиден телефонен номер"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Телефонен номер</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+359 88 123 4567" {...field} />
            </FormControl>
            <FormDescription>
              Ще се свържем с вас за потвърждение
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email_address" rules={{
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Невалиден имейл адрес"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Имейл</FormLabel>
            <FormControl>
              <Input type="email" placeholder="ivan@example.com" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="order_details" rules={{
      required: "Моля, опишете поръчката си",
      minLength: {
        value: 5,
        message: "Минимум 5 символа"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Какво желаете да поръчате?</FormLabel>
            <FormControl>
              <Textarea placeholder="Опишете вашата поръчка – ястия, количества, специални изисквания..." rows={4} {...field} />
            </FormControl>
            <FormDescription>
              Колкото по‑подробно, толкова по‑лесно ще обработим поръчката
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="order_type" rules={{
      required: "Моля, изберете тип поръчка"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Тип поръчка</FormLabel>
            <FormControl>
              <select {...field} className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm">
                <option value="" disabled>Изберете тип</option>
                <option value="Доставка">Доставка</option>
                <option value="Вземане от място">Вземане от място</option>
                <option value="На място">На място</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="address" rules={{
      required: "Адресът е задължителен"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Адрес за доставка</FormLabel>
            <FormControl>
              <Input placeholder="гр. София, ул. ..." {...field} />
            </FormControl>
            <FormDescription>
              Въведете точен адрес или уточнение за получаване
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="text-lg font-bold text-primary mb-4 text-center">
        Фиксирана доставка: 2 евро
      </div>

      {submitError && <div className="p-4 bg-[hsl(15_85%_48%/0.1)] border border-[hsl(15_85%_48%/0.3)] rounded-lg">
          <p className="text-[hsl(15_85%_43%)] text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full btn-primary">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Изпращане...
          </> : "Изпрати поръчка"}
      </Button>
    </Form>;
}